import type { KeyboardEvent } from "react";
import { BaseBotData, BasicBotData, BotType, MarginTypes } from "@shared/types";
import { getBotLongShortDisplay } from "@shared/features/bots/utils";
import { MARGIN_EXCHANGES } from "@shared/common/constants";
import { translateOptionName } from "@shared/common/i18n/optionNames";

/** The new-look bot card's measures (radius 12, padding 22/24/20, logo 40). */
export const NEW_LOOK_BOT_CARD = {
  radius: 12,
  /** Side padding; the chart and the footer bar run out to the edges. */
  padding: 24,
  paddingTop: 22,
  paddingBottom: 20,
  logo: 40,
  logoRadius: 12,
  /** The PnL area chart of the signals / TradingView cards. */
  chartHeight: 64,
};

/** The label-to-value gap of the stat rows (see newLookNowPanelStyles). */
export const NEW_LOOK_NOW_PANEL = {
  labelGap: 4,
};

const getMarginTypeDisplay = (marginType?: BasicBotData["margin_type"]) =>
  marginType
    ? translateOptionName(
        marginType === MarginTypes.CROSS ? "Cross" : "Isolated"
      )
    : undefined;

/**
 * The bot's direction and margin as separate parts (["Long", "Isolated 3x"]),
 * with the same rules and wording as the classic MarginData.
 */
export const getNewLookMarginParts = (
  botData: BasicBotData | BaseBotData
): string[] => {
  const parts: string[] = [];
  if (botData.bot_type !== BotType.HEDGING) {
    parts.push(getBotLongShortDisplay(botData));
  }
  const marginType = getMarginTypeDisplay(botData.margin_type);
  const leverage = botData.leverage ? `${botData.leverage}x` : undefined;
  const margin = [marginType, leverage].filter(Boolean).join(" ");
  if (margin) parts.push(margin);
  return parts;
};

export interface NewLookSettingsChip {
  key: string;
  text: string;
  title?: string;
}

/**
 * The bot card's settings chips: direction ("Long", "Short", "Long/Short"
 * or "Spot"; none for hedging bots, which are both by design) and, on
 * futures (margin) exchanges only, margin ("Isolated 3x"). Same wording as
 * MarginData.
 */
export const getNewLookSettingsChips = (
  botData: BasicBotData | BaseBotData,
  longShortLabel: string
): NewLookSettingsChip[] => {
  const chips: NewLookSettingsChip[] = [];
  const exchange = botData.exchange ?? botData.exchanges?.[0];
  const isMargin = MARGIN_EXCHANGES.has(exchange);
  if (botData.bot_type !== BotType.HEDGING) {
    const isBoth =
      isMargin && (botData.reverse === null || botData.reverse === undefined);
    chips.push({
      key: "side",
      text: isBoth ? longShortLabel : getBotLongShortDisplay(botData),
    });
  }
  // Margin mode and leverage only exist on futures (margin) markets.
  if (!isMargin) return chips;
  const marginType = getMarginTypeDisplay(botData.margin_type);
  const leverage = botData.leverage ? `${botData.leverage}x` : undefined;
  const margin = [marginType, leverage].filter(Boolean).join(" ");
  if (margin) chips.push({ key: "margin", text: margin });
  return chips;
};

/** The bot's direction and margin on one line ("Long · Isolated 3x"). */
export const getNewLookMarginText = (botData: BasicBotData | BaseBotData) =>
  getNewLookMarginParts(botData).join(" · ");

/** Enter / Space on a focused card opens it, like a click. */
export const onCardKeyDown =
  (open: () => void) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  };

/** 66900 -> "66,900" (plain decimal numbers only; anything else as is). */
export const withThousands = (value: string | number | undefined) => {
  const text = String(value ?? "");
  if (!/^-?\d+(\.\d+)?$/.test(text)) return text;
  const [integer, decimals] = text.split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimals === undefined ? grouped : `${grouped}.${decimals}`;
};

const COMPACT_UNITS = [1e12, 1e9, 1e6, 1e3];
const compactFormats = new Map<number, Intl.NumberFormat>();

/**
 * A chart axis value in compact form ("−$1.2K", "₿0.004", "12%") with as many
 * decimals as the step between the axis guides needs, so guides that sit
 * close together don't all collapse to the same label ("₿0", "$0").
 */
export const compactAxisValue = (
  value: number,
  step: number,
  { prefix = "", suffix = "" }: { prefix?: string; suffix?: string } = {}
) => {
  const abs = Math.abs(value);
  // The step in the unit the compact form writes the value in (K, M, …).
  const unitStep = Math.abs(step) / (COMPACT_UNITS.find((u) => abs >= u) ?? 1);
  const digits =
    unitStep > 0 && Number.isFinite(unitStep)
      ? Math.min(8, Math.max(0, Math.ceil(-Math.log10(unitStep))))
      : 1;
  let format = compactFormats.get(digits);
  if (!format) {
    format = new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: digits,
    });
    compactFormats.set(digits, format);
  }
  return `${value < 0 ? "−" : ""}${prefix}${format.format(abs)}${suffix}`;
};

/** 1.6 -> "+1.6%", -6.5 -> "−6.5%" (typographic minus), 0 -> "0%". */
export const formatSignedPercent = (value: number) =>
  `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(value)}%`;

/**
 * Whether a series makes a readable sparkline: at least three points that
 * don't all have the same value. Anything less reads as a broken chart, so
 * callers render nothing instead (and give its space to the figure).
 */
export const canDrawSparkline = (values?: number[]): values is number[] => {
  if (!values || values.length < 3) return false;
  return Math.min(...values) !== Math.max(...values);
};

/**
 * At most `max` evenly spaced samples of a series (the first and last kept),
 * so a long series draws as a calm line; shorter series are left as they are.
 */
export const resampleSeries = (values: number[], max = 40, target = 32) => {
  if (values.length <= max) return values;
  const last = values.length - 1;
  return Array.from(
    { length: target },
    (_, index) => values[Math.round((index / (target - 1)) * last)]
  );
};
